// src/hooks/useProviderProfile.ts
// ✅ MODERN IMPLEMENTATION - No legacy compatibility, clean architecture

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { providerService } from '@/lib/api/providerService';

// =============================================================================
// CLEAN TYPE DEFINITIONS
// =============================================================================

export interface BusinessProfile {
  // Primary Business Information
  business_name: string;
  description: string;
  about_me: string;
  business_type: 'individual' | 'small_business' | 'company' | 'llc' | 'corporation';
  provider_slug_url?: string;
  
  // Contact Information
  business_phone: string;
  business_email: string;
  website?: string;
  address?: string;
  city?: string;
  
  // Professional Details
  years_experience: number;
  specializations?: string;
  certifications?: string;
  
  // Business Registration & Legal
  business_license?: string;
  insurance_details?: string;
  
  // Profile & Display
  profile_image?: string;
  
  // User Information (if editing user details too)
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  preferred_language?: string;
}

export interface ProviderProfile {
  provider_id: string | number;
  user_id: string | number;
  business_name: string;
  business_type: string;
  business_email: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  provider_slug_url?: string;
  description: string;
  years_experience: string | number;
  is_verified: string | boolean;
  is_active: string | boolean;
  rating: string | number;
  total_reviews: string | number;
  profile_picture?: string;
  about_me: string;
  registration_date?: string;
  is_featured?: string | boolean;
  created_at: string;
  updated_at: string;
  phone_number?: string;
  status?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  business_license?: string;
  insurance_details?: string;
  specializations?: string;
  certifications?: string;
  is_available?: string | boolean;
  services_count?: number;
  areas_count?: number;
  // Additional fields from API response
  business_phone?: string;
  profile_image?: string;
  verification_status?: 'pending' | 'verified' | 'rejected';
  // User information
  first_name?: string;
  last_name?: string;
  email?: string;
  preferred_language?: string;
}

export interface SetupStatus {
  profile_complete: boolean;
  has_services: boolean;
  has_areas: boolean;
  is_complete: boolean;
  completion_percentage: number;
  next_steps: string[];
}

export interface ProviderProfileResponse {
  provider: ProviderProfile;
  services_count: number;
  areas_count: number;
  setup_required: boolean;
  user: any; // User data from backend
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const profileKeys = {
  all: ['provider-profile'] as const,
  current: () => [...profileKeys.all, 'current'] as const,
  slug: (slug: string) => [...profileKeys.all, 'slug', slug] as const,
  verification: () => [...profileKeys.all, 'verification'] as const,
} as const;

// =============================================================================
// CORE HOOKS
// =============================================================================

export const useProviderProfile = () => {
  return useQuery({
    queryKey: profileKeys.current(),
    queryFn: async (): Promise<ProviderProfileResponse> => {
      const response = await providerService.getProfile();
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch profile');
      }
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error: any) => 
      error?.status !== 401 && error?.status !== 403 && failureCount < 3,
  });
};

export const useUpdateProviderProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<BusinessProfile>) => {
      const response = await providerService.updateProviderProfile(data);
      if (!response.success) {
        throw new Error(response.message || 'Failed to update profile');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(profileKeys.current(), data);
      queryClient.invalidateQueries({ queryKey: profileKeys.all });
      toast.success('Profile updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useUploadProfileImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const response = await providerService.uploadProfileImage(file);
      if (!response.success) {
        throw new Error(response.message || 'Failed to upload image');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.current() });
      toast.success('Profile image updated');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useSlugOperations = () => {
  const queryClient = useQueryClient();

  const checkAvailability = useMutation({
    mutationFn: async (slug: string) => {
      const response = await providerService.checkSlugAvailability(slug);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });

  const updateSlug = useMutation({
    mutationFn: async (slug: string) => {
      const response = await providerService.updateSlug(slug);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.current() });
      toast.success('Provider URL updated');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const generateSuggestions = useMutation({
    mutationFn: async (businessName: string) => {
      const response = await providerService.generateSlugSuggestions(businessName);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });

  return {
    checkAvailability,
    updateSlug,
    generateSuggestions,
  };
};

export const useProviderBySlug = (slug: string, enabled = true) => {
  return useQuery({
    queryKey: profileKeys.slug(slug),
    queryFn: async () => {
      const response = await providerService.getProviderBySlug(slug);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    enabled: enabled && !!slug,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => 
      error?.status !== 404 && failureCount < 2,
  });
};

// =============================================================================
// COMPUTED HOOKS
// =============================================================================

export const useSetupProgress = () => {
  const { data, isLoading } = useProviderProfile();

  if (isLoading || !data) {
    return {
      isLoading: true,
      progress: 0,
      isComplete: false,
      nextSteps: [],
      isFirstTime: false,
    };
  }

  const { provider, setup_required } = data;

  // Calculate completion progress based on provider data
  let completedFields = 0;
  const totalFields = 8; // Total required fields for complete profile

  const requiredFields = [
    'business_name',
    'description', 
    'about_me',
    'business_type',
    'years_experience',
    'phone_number', // Backend uses phone_number, not business_phone
    'business_email'
  ];

  // Count completed required fields
  if (provider) {
    requiredFields.forEach(field => {
      if (provider[field] && String(provider[field]).trim() !== '') {
        completedFields++;
      }
    });

    // Additional check for experience being greater than 0
    if (provider.years_experience && Number(provider.years_experience) > 0) {
      completedFields++; // This will be the 8th field
    } else {
      completedFields--; // Adjust if years_experience was counted but is 0
    }
  }

  const progress = Math.round((completedFields / totalFields) * 100);
  const isComplete = progress === 100;

  // Generate next steps based on missing fields
  const nextSteps: string[] = [];
  if (provider) {
    if (!provider.business_name) nextSteps.push('Add business name');
    if (!provider.description) nextSteps.push('Add business description');
    if (!provider.about_me) nextSteps.push('Add about me section');
    if (!provider.business_type) nextSteps.push('Select business type');
    if (!provider.years_experience || Number(provider.years_experience) <= 0) nextSteps.push('Add years of experience');
    if (!provider.phone_number) nextSteps.push('Add business phone');
    if (!provider.business_email) nextSteps.push('Add business email');
  }

  return {
    isLoading: false,
    progress,
    isComplete,
    nextSteps,
    isFirstTime: setup_required || false,
    setupStatus: {
      completion_percentage: progress,
      is_complete: isComplete,
      next_steps: nextSteps
    },
  };
};

export const useProfileValidation = () => {
  const { data } = useProviderProfile();

  const validateProfile = (profile: Partial<BusinessProfile>) => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!profile.business_name?.trim()) {
      errors.business_name = 'Business name is required';
    }

    if (!profile.description?.trim()) {
      errors.description = 'Business description is required';
    }

    if (!profile.business_phone?.trim()) {
      errors.business_phone = 'Business phone is required';
    }

    if (!profile.business_email?.trim()) {
      errors.business_email = 'Business email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.business_email)) {
      errors.business_email = 'Please enter a valid email address';
    }

    if (!profile.about_me?.trim()) {
      errors.about_me = 'About section is required';
    }

    // Optional validations
    if (profile.website && !/^https?:\/\/.+/.test(profile.website)) {
      errors.website = 'Please enter a valid website URL (starting with http:// or https://)';
    }

    if ((profile.years_experience || 0) < 1) {
      errors.years_experience = 'Experience must be at least 1 year';
    }

    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      errors.email = 'Please enter a valid personal email address';
    }

    if (profile.provider_slug_url && !/^[a-zA-Z0-9-_]+$/.test(profile.provider_slug_url)) {
      errors.provider_slug_url = 'URL slug can only contain letters, numbers, hyphens, and underscores';
    }

    // Business name length validation
    if (profile.business_name && (profile.business_name.length < 2 || profile.business_name.length > 255)) {
      errors.business_name = 'Business name must be between 2 and 255 characters';
    }

    // Description length validation
    if (profile.description && profile.description.length > 1000) {
      errors.description = 'Description cannot exceed 1000 characters';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  return {
    validateProfile,
    currentProfile: data?.provider,
  };
};